import { useMemo, useState } from "react";
import Release2BIntake from "./Release2BIntake.jsx";
import {
  FICTIONAL_ROOTS_LISTINGS,
  applyLinkedListing,
  clone,
  createListingRecord,
  createSession
} from "./release2BState.js";
import {
  needsChangesMarketingReadOnly,
  needsChangesOpenHouseReadOnly
} from "./release2BQcCorrections.js";

const OPEN_HOUSE_FIELD_LABELS = new Set(["Requested Date", "Start Time", "End Time", "Host"]);

function isOpenHouseControl(target) {
  if (!target?.closest) return false;
  const button = target.closest("button");
  if (button && ["Yes", "No"].includes(String(button.textContent || "").trim())) return true;
  const label = target.closest("label.r2b-field");
  if (!label) return false;
  const fieldLabel = String(label.querySelector("span")?.textContent || "").trim();
  return OPEN_HOUSE_FIELD_LABELS.has(fieldLabel);
}

export default function Release2BIntakeGuarded(props) {
  const { session, setSession } = props;
  const [listingQuery, setListingQuery] = useState("");
  const record = session?.record;
  const onAncillaryStep = record?.step === 3;
  const marketingReadOnly = onAncillaryStep && needsChangesMarketingReadOnly(record);
  const openHouseReadOnly = onAncillaryStep && needsChangesOpenHouseReadOnly(record);
  const ancillaryReadOnly = marketingReadOnly || openHouseReadOnly;
  const sellerListingSearch = record?.kind === "seller" && record?.step === 0 && !record.linkedListing;
  const matches = useMemo(() => {
    const needle = listingQuery.trim().toLowerCase();
    if (!needle) return FICTIONAL_ROOTS_LISTINGS;
    return FICTIONAL_ROOTS_LISTINGS.filter(listing => (
      String(listing.address || "").toLowerCase().includes(needle)
      || String(listing.mls || "").toLowerCase().includes(needle)
      || String(listing.id || "").toLowerCase().includes(needle)
    ));
  }, [listingQuery]);

  function blockAncillaryEvent(event) {
    if (!ancillaryReadOnly || !event.target?.closest?.(".r2b-marketing-layout")) return;
    const openHouseControl = isOpenHouseControl(event.target);
    const shouldBlock = openHouseControl ? openHouseReadOnly : marketingReadOnly;
    if (!shouldBlock) return;
    event.preventDefault();
    event.stopPropagation();
  }

  function selectListing(listing) {
    setSession(current => {
      const next = clone(current);
      next.record = applyLinkedListing(next.record, listing);
      return next;
    });
  }

  function startListingAgreement() {
    setSession(() => createSession(createListingRecord()));
  }

  let ancillaryMessage = "";
  if (marketingReadOnly && openHouseReadOnly) ancillaryMessage = "Existing Marketing and Open House lifecycle state is read-only during Base Needs Changes. Use each dedicated correction entry point to edit those streams.";
  else if (marketingReadOnly) ancillaryMessage = "Existing Marketing lifecycle state is read-only during Base Needs Changes. A new Open House request remains available until Open House enters its own lifecycle.";
  else if (openHouseReadOnly) ancillaryMessage = "Existing Open House lifecycle state is read-only during Base Needs Changes. Marketing protection is evaluated independently.";

  return (
    <div
      className={`${ancillaryReadOnly ? "r2b-needs-ancillary-readonly " : ""}${sellerListingSearch ? "r2b-seller-search-active" : ""}`}
      onClickCapture={blockAncillaryEvent}
      onChangeCapture={blockAncillaryEvent}
      onInputCapture={blockAncillaryEvent}
      onKeyDownCapture={blockAncillaryEvent}
    >
      {ancillaryReadOnly && (
        <div className="r2b-notice r2b-notice--warn" style={{ margin: "18px 24px 0" }}>
          <strong>Base Needs Changes preserves independent ancillary workflow state.</strong><br />
          {ancillaryMessage}
        </div>
      )}
      {sellerListingSearch && (
        <section className="r2b-card" style={{ margin: "18px 24px 0" }}>
          <h3>Find Existing ROOTS Listing / Pre-List</h3>
          <label className="r2b-field">
            <span>Property address, MLS, or ROOTS listing reference</span>
            <input value={listingQuery} onChange={event => setListingQuery(event.target.value)} placeholder="Search existing ROOTS listings" />
          </label>
          {matches.length > 0 ? (
            <div className="r2b-search-results" style={{ marginTop: 10 }}>
              {matches.map(listing => (
                <button key={listing.id} type="button" className="r2b-search-result" onClick={() => selectListing(listing)}>
                  <span><strong>{listing.address}</strong><small>{listing.status} · {listing.mls}</small></span><b>Use Listing</b>
                </button>
              ))}
            </div>
          ) : (
            <div className="r2b-notice r2b-notice--error" style={{ marginTop: 10 }}>
              <strong>Listing not found in ROOTS.</strong><br />
              This seller must first be submitted through New Listing Agreement before a Seller Pending transaction can be created.
              <div style={{ marginTop: 10 }}><button type="button" className="button primary" onClick={startListingAgreement}>Start New Listing Agreement</button></div>
            </div>
          )}
          <p className="r2b-support" style={{ marginTop: 10 }}>Selecting the listing preserves ROOTS transaction identity only. Primary Seller selection remains a separate explicit live Follow Up Boss search.</p>
        </section>
      )}
      {sellerListingSearch && <style>{`.r2b-seller-search-active .r2b-full-page .r2b-search-results{display:none}.r2b-seller-search-active>section .r2b-search-results{display:block}`}</style>}
      <Release2BIntake {...props} />
    </div>
  );
}
